import {
  Alert,
  Box,
  CircularProgress,
  List,
  ListItemButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, type UIEvent } from "react";

import { formatRelativeConversationTime, type ScoutThreadListItem } from "./useScoutThreadsList";

const LOAD_MORE_ROW_MIN_HEIGHT_PX = 72;
const LOAD_MORE_TRIGGER_BUFFER_PX = 8;
const SEARCH_SCROLLBAR_WIDTH_PX = 12;
const SEARCH_SCROLLBAR_OUTSET_PX = 24;
const LOAD_MORE_CONTENT_RIGHT_INSET_PX = SEARCH_SCROLLBAR_OUTSET_PX + 16;
const UNTITLED_CHAT_LABEL = "Untitled chat";
const LOAD_MORE_LABEL = "Load more";

export function ScoutThreadListSection({
  heading,
  items,
  loading,
  hasMore,
  loadingMore,
  emptyMessage,
  errorMessage,
  onLoadMore,
  onSelectItem,
  compact = false,
}: {
  heading: string;
  items: ScoutThreadListItem[];
  loading?: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  emptyMessage: string;
  errorMessage?: string;
  onLoadMore: () => Promise<void> | void;
  onSelectItem: (item: ScoutThreadListItem) => void;
  compact?: boolean;
}) {
  const theme = useTheme();
  const scrollSurfaceColor = theme.palette.background.paper;
  const scrollThumbColor = theme.palette.scrollbarThumb;
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (loading || loadingMore || !hasMore) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      const isNearBottom = distanceFromBottom <= LOAD_MORE_TRIGGER_BUFFER_PX;

      if (isNearBottom) {
        void onLoadMore();
      }
    },
    [hasMore, loading, loadingMore, onLoadMore]
  );

  return (
    <Stack
      spacing={1.5}
      paddingBottom={theme.spacing(1)}
      sx={{ flex: 1, minHeight: 0, height: "100%" }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography
          variant="caption"
          sx={{
            textTransform: "uppercase",
            letterSpacing: 0.6,
            fontWeight: 500,
            color: "text.secondary",
          }}
        >
          {heading}
        </Typography>
      </Stack>
      <Box
        sx={{
          flex: 1,
          height: 0,
          width: compact ? "100%" : `calc(100% + ${SEARCH_SCROLLBAR_OUTSET_PX}px)`,
          maxWidth: "none",
          marginRight: compact ? 0 : `-${SEARCH_SCROLLBAR_OUTSET_PX}px`,
          minHeight: 0,
          backgroundColor: scrollSurfaceColor,
          overflow: "auto",
          overflowX: "hidden",
          scrollbarGutter: "stable",
          scrollbarWidth: "thin",
          scrollbarColor: `${scrollThumbColor} ${scrollSurfaceColor}`,
          "&::-webkit-scrollbar": {
            width: `${SEARCH_SCROLLBAR_WIDTH_PX}px`,
            backgroundColor: scrollSurfaceColor,
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: scrollSurfaceColor,
            border: "none",
            boxShadow: "none",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: scrollThumbColor,
            border: "none",
            borderRadius: 999,
          },
          "&::-webkit-scrollbar-corner": {
            backgroundColor: scrollSurfaceColor,
          },
        }}
        data-testid="thread-list-scroll-container"
        onScroll={handleScroll}
      >
        {loading ? (
          <List disablePadding>
            {Array.from({ length: 5 }, (_, i) => (
              <ListItemButton key={i}>
                <Skeleton
                  data-testid="thread-list-skeleton"
                  variant="text"
                  width="100%"
                  height={40}
                />
              </ListItemButton>
            ))}
          </List>
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : items.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {items.map((item) => {
              const relativeUpdatedAt = formatRelativeConversationTime(item.updatedAt);
              const hasPreviewExcerpt = !compact && Boolean(item.previewExcerpt);
              const titleLabel = item.title ?? UNTITLED_CHAT_LABEL;

              return (
                <ListItemButton
                  key={item.id}
                  onClick={() => onSelectItem(item)}
                  sx={{
                    width: "100%",
                    alignItems: "stretch",
                    borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    px: compact ? 1.5 : 2,
                    py: compact ? 2 : 1.4,
                    "&:last-child": {
                      borderBottom: "none",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "baseline",
                      gap: 1.25,
                    }}
                  >
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1.1,
                      }}
                    >
                      {compact ? (
                        <Tooltip
                          placement="left"
                          title={
                            <Stack spacing={0.5}>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {titleLabel}
                              </Typography>
                              {hasPreviewExcerpt ? (
                                <Typography variant="caption">{item.previewExcerpt}</Typography>
                              ) : null}
                              {relativeUpdatedAt ? (
                                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                                  {relativeUpdatedAt}
                                </Typography>
                              ) : null}
                            </Stack>
                          }
                        >
                          <Typography
                            variant="body2"
                            color="text.primary"
                            sx={{
                              fontWeight: 500,
                              minWidth: 0,
                              flex: 1,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {titleLabel}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{
                            fontWeight: 500,
                            flexShrink: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {titleLabel}
                        </Typography>
                      )}

                      {hasPreviewExcerpt && !compact && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            minWidth: 0,
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            opacity: 0.72,
                          }}
                        >
                          {item.previewExcerpt}
                        </Typography>
                      )}
                    </Box>

                    {!compact && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          flexShrink: 0,
                          whiteSpace: "nowrap",
                          opacity: 0.58,
                        }}
                      >
                        {relativeUpdatedAt}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              );
            })}
            {(hasMore || loadingMore) && (
              <ListItemButton
                disabled={loadingMore}
                onClick={() => {
                  if (!loadingMore) {
                    void onLoadMore();
                  }
                }}
                sx={{
                  minHeight: `${LOAD_MORE_ROW_MIN_HEIGHT_PX}px`,
                  justifyContent: "center",
                  borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                  pr: compact ? 1.5 : `${LOAD_MORE_CONTENT_RIGHT_INSET_PX}px`,
                }}
              >
                {loadingMore ? (
                  <Stack
                    width="100%"
                    direction="row"
                    justifyContent="center"
                    alignItems="center"
                    gap={1}
                  >
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">
                      Loading more
                    </Typography>
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {LOAD_MORE_LABEL}
                  </Typography>
                )}
              </ListItemButton>
            )}
          </List>
        )}
      </Box>
    </Stack>
  );
}
